import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import LightningLandersBuilder from "features/lightningLanders/components/LightningLandersBuilder";
import { useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { saveComponent, readComponent } from "features/campaigns";
import LightningLanderProvider, {
  useLightningLanderContext,
} from "features/lightningLanders/providers/LightningLanderProvider";
import {
  ApiComponentType,
  ApiProjectSettingKey,
  ApiReadLanderResponse,
  ApiSimpleComponent,
} from "@incendium/api";
import { landersService } from "Apis";
import { useLanderComponents } from "features/lightningLanders/hooks/useLanderComponents";
import { useSubdomains } from "Hooks/useSubdomains";
import { updateSmartLink } from "features/rezonate/services/smartLinkService";
import { ApiUTMEntityLevel } from "@incendium/api";
import {
  landerDefaultTheme,
  LightningLanderThemeProvider,
  useLightningLanderThemeSettings,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import NewLanderDialog from "features/lightningLanders/components/NewLanderDialog";
import { useLanderTemplates } from "features/lightningLanders/hooks/useLanderTemplates";
import { isEmpty } from "Helpers/isEmpty";
import { generatePathFromName } from "features/lightningLanders/utils";
import {
  getSelectableSubdomains,
} from "features/subdomains/utils";

function LightningLanderPageInner() {
  const { componentId } = useParams<{ componentId: string }>();
  const { selectedProject, projectSettings } = useSelectedProject();
  const { setComponents } = useLanderComponents();
  const history = useHistory();
  const location = useLocation();
  const { component, setComponent, setLander } = useLightningLanderContext();
  const { setTheme: setCustomTheme } = useLightningLanderThemeSettings();
  const [showNewDialog, setShowNewDialog] = useState(false);
  const { templates } = useLanderTemplates();
  const { subdomains } = useSubdomains();
  const hasShownDialog = useRef(false);

  const onEditSaved = useCallback(
    (component) => {
      setComponent(component);
      setComponents((components) =>
        produce(components, (draft) => {
          if (!component) {
            return;
          }
          const idx = draft.findIndex((d) => d.id === component?.id);
          if (idx >= 0) {
            draft[idx] = { ...draft[idx], ...component };
          }
        }),
      );
    },
    [setComponents, setComponent],
  );

  const onLanderSaved = useCallback(
    (lander: ApiReadLanderResponse) => {
      setComponents((components) =>
        produce(components, (draft) => {
          if (!lander) {
            return;
          }
          // Find component by componentId in the lander
          const idx = draft.findIndex((d) => d.id === lander.componentId);
          if (idx >= 0) {
            // Update the path property of the component
            draft[idx].path = lander;
            if (component.name) {
              draft[idx].name = component.name;
            }
          }
        }),
      );
    },
    [setComponents, component.name],
  );

  const handleCreateLander = useCallback(
    async (
      name: string,
      templateId?: number,
      aiComponent?: ApiSimpleComponent,
      adId?: number,
      createSmartLink?: boolean,
      subdomainId?: number,
    ) => {
      if (!selectedProject?.id) return;
      if (isEmpty(projectSettings)) {
        return;
      }

      let componentToUse: ApiSimpleComponent | undefined = aiComponent;

      // If AI component is not provided, create a new component (template or blank)
      if (!componentToUse?.id) {
        const template = templates.find((t) => t.id === templateId);
        const { id, ...rest } = template || {};
        // remove id, as we are creating a new one not patching the template
        const payload = {
          ...rest,
          name,
          type: ApiComponentType.LANDING,
          theme:
            projectSettings[ApiProjectSettingKey.LANDING_THEME] ||
            JSON.stringify(landerDefaultTheme),
        };

        const res = await saveComponent(selectedProject.id, payload);
        componentToUse = res.component;
      }

      if (!componentToUse?.id) {
        return;
      }

      const upsertComponentAndNavigate = () => {
        setComponents((components) =>
          produce(components, (draft) => {
            const idx = draft.findIndex((d) => d.id === componentToUse!.id);
            if (idx >= 0) {
              draft[idx] = { ...draft[idx], ...componentToUse! };
            } else {
              draft.push(componentToUse!);
            }
          }),
        );
        const newPath = location.pathname.replace(
          /\/new$/,
          `/${componentToUse!.id}`,
        );
        history.push(newPath);
      };

      const selectableSubdomains = getSelectableSubdomains(subdomains);
      const resolvedSubdomainId =
        subdomainId ??
        (selectableSubdomains.length === 1
          ? selectableSubdomains[0].id
          : undefined);

      if (!resolvedSubdomainId) {
        upsertComponentAndNavigate();
        return;
      }

      // Check if landing path already exists (might have been created during polling)
      let landerRes: ApiReadLanderResponse;
      try {
        const existingLander =
          await landersService.landersServiceReadLanderByComponent({
            projectId: selectedProject.id,
            componentId: componentToUse.id,
          });

        if (existingLander?.id) {
          // Landing path already exists, use it
          landerRes = existingLander;
        } else {
          // Create landing path
          const generatedPath = generatePathFromName(name);
          landerRes = await landersService.landersServiceCreateLander({
            projectId: selectedProject.id,
            payload: {
              componentId: componentToUse.id,
              subdomainId: resolvedSubdomainId,
              path: generatedPath,
              description: name,
            },
          });
        }
      } catch (error: any) {
        // If error is because path already exists, try to read it
        if (
          error?.status === 409 ||
          error?.message?.includes("already exists")
        ) {
          try {
            landerRes =
              await landersService.landersServiceReadLanderByComponent({
                projectId: selectedProject.id,
                componentId: componentToUse.id,
              });
          } catch (readError) {
            console.error("Failed to read existing landing path:", readError);
            throw error; // Throw original error
          }
        } else {
          throw error;
        }
      }

      // Create smart link if requested
      if (createSmartLink && adId) {
        try {
          await updateSmartLink(
            selectedProject.id,
            ApiUTMEntityLevel.AD,
            adId,
            landerRes.id,
            undefined,
          );
        } catch (error) {
          console.error("Failed to create smart link:", error);
          // Continue even if smart link creation fails
        }
      }

      // Add or update component in list with the landing path
      setComponents((components) =>
        produce(components, (draft) => {
          const componentWithPath = { ...componentToUse!, path: landerRes };
          const idx = draft.findIndex((d) => d.id === componentToUse!.id);
          if (idx >= 0) {
            draft[idx] = { ...draft[idx], ...componentWithPath };
          } else {
            draft.push(componentWithPath);
          }
        }),
      );

      const newPath = location.pathname.replace(
        /\/new$/,
        `/${componentToUse.id}`,
      );
      history.push(newPath);
    },
    [
      selectedProject?.id,
      location.pathname,
      history,
      setComponents,
      templates,
      projectSettings,
      subdomains,
    ],
  );

  useEffect(() => {
    if (!selectedProject?.id || Boolean(component.id)) {
      return;
    }

    if (componentId === "new" && !hasShownDialog.current) {
      hasShownDialog.current = true;
      setShowNewDialog(true);
      return;
    }

    const readLander = async () => {
      const cRes = await readComponent(
        selectedProject?.id as number,
        Number(componentId),
      );

      if (cRes.component?.theme) {
        setCustomTheme(JSON.parse(cRes.component?.theme || "{}")); // Set default theme if not present
      }
      setComponent(cRes.component as ApiSimpleComponent);

      const pathRes = await landersService.landersServiceReadLanderByComponent({
        projectId: selectedProject?.id as number,
        componentId: Number(componentId),
      });

      setLander(pathRes);
    };

    if (componentId !== "new") {
      readLander();
    }
  }, [
    selectedProject,
    history,
    location,
    componentId,
    setComponent,
    setLander,
    setComponents,
    component.id,
    setCustomTheme,
  ]);

  const onConfirm = useCallback(
    (
      name: string,
      templateId?: number,
      aiComponent?: ApiSimpleComponent,
      adId?: number,
      createSmartLink?: boolean,
      subdomainId?: number,
    ) => {
      setShowNewDialog(false);
      handleCreateLander(
        name,
        templateId,
        aiComponent,
        adId,
        createSmartLink,
        subdomainId,
      );
    },
    [handleCreateLander],
  );

  const onComponentCreated = useCallback(
    async (
      component: ApiSimpleComponent,
      name?: string,
      adId?: number,
      createSmartLink?: boolean,
      subdomainId?: number,
    ) => {
      // Add or update component in list without navigating
      setComponents((components) =>
        produce(components, (draft) => {
          const idx = draft.findIndex((d) => d.id === component.id);
          if (idx >= 0) {
            draft[idx] = { ...draft[idx], ...component };
          } else {
            draft.push(component);
          }
        }),
      );

      // Create landing path immediately when a subdomain was chosen
      if (component?.id && selectedProject?.id && name && subdomainId) {
        try {
          const generatedPath = generatePathFromName(name);
          const landerRes = await landersService.landersServiceCreateLander({
            projectId: selectedProject.id,
            payload: {
              componentId: component.id,
              subdomainId,
              path: generatedPath,
              description: name,
            },
          });

          // Create smart link if requested
          if (createSmartLink && adId) {
            try {
              await updateSmartLink(
                selectedProject.id,
                ApiUTMEntityLevel.AD,
                adId,
                landerRes.id,
                undefined,
              );
            } catch (error) {
              console.error("Failed to create smart link:", error);
            }
          }

          // Update component with path
          setComponents((components) =>
            produce(components, (draft) => {
              const idx = draft.findIndex((d) => d.id === component.id);
              if (idx >= 0) {
                draft[idx].path = landerRes;
              }
            }),
          );
        } catch (error) {
          console.error(
            "Failed to create landing path for pending component:",
            error,
          );
          // Don't throw - component still exists, path can be created later
        }
      }
    },
    [setComponents, selectedProject?.id],
  );

  if (!selectedProject) {
    return (
      <CenterPage calcHeight>
        <Loading />
      </CenterPage>
    );
  }

  // Don't show loading when creating new lander
  if (!component?.id && componentId !== "new") {
    return (
      <CenterPage calcHeight>
        <Loading />
      </CenterPage>
    );
  }

  return (
    <>
      <NewLanderDialog
        open={showNewDialog}
        onClose={() => {
          setShowNewDialog(false);
          history.push("../lightning-landing-pages");
        }}
        onConfirm={onConfirm}
        onComponentCreated={onComponentCreated}
      />
      {component?.id && (
        <LightningLandersBuilder
          saveToken={null}
          project={selectedProject}
          onSaved={onEditSaved}
          onLanderSaved={onLanderSaved}
        />
      )}
    </>
  );
}

function LightningLanderPage() {
  return (
    <LightningLanderProvider>
      <LightningLanderThemeProvider>
        <LightningLanderPageInner />
      </LightningLanderThemeProvider>
    </LightningLanderProvider>
  );
}

export default LightningLanderPage;
